import type { RosterAgent } from "@ark-bot/protocol";

import { text } from "./i18n.js";
import { orderRoster } from "./roster.js";
import { MENTIONS } from "./text/chat.js";

/**
 * The composer's @ list, as the prototype's mention suggestions. In a chat
 * with one agent it offers the user's other agents: the host puts a note
 * naming whoever the message mentions, with their ids, ahead of it, so "ask
 * @Scout" is all the agent needs to reach Scout. In a room it offers the
 * members and everyone, and the mentions decide who answers. The host reads
 * both from the text as typed, so a pick inserts nothing but `@Name`.
 */

/** One row of the @ list. */
export interface Mentionable {
  readonly id: string;
  readonly name: string;
  /** The row's second line: the agent's title, else the start of its description. */
  readonly detail: string;
  /** The whole room rather than one member. */
  readonly everyone?: true;
}

/** The whole room. Its line is read when the list is drawn, in the language the app is in then. */
export const EVERYONE: Mentionable = {
  id: "everyone",
  name: "everyone",
  get detail() {
    return text(MENTIONS).everyoneDetail;
  },
  everyone: true,
};

const WORD = /[a-z0-9]/i;

function firstLine(text: string | undefined): string {
  return (text ?? "").trim().split("\n")[0]?.trim() ?? "";
}

/** Who can be mentioned in this chat: a room's members and everyone, or every other single agent, most recently active first. */
export function mentionablesFor(chat: RosterAgent, agents: readonly RosterAgent[]): Mentionable[] {
  const row = (agent: RosterAgent): Mentionable => {
    const title = firstLine(agent.config.title);
    return { id: agent.id, name: agent.name, detail: title.length > 0 ? title : firstLine(agent.config.description) };
  };
  if (chat.kind === "group") {
    const byId = new Map(agents.map((agent) => [agent.id, agent]));
    const members = chat.members.flatMap((id) => {
      const agent = byId.get(id);
      return agent == null ? [] : [row(agent)];
    });
    return members.length === 0 ? [] : [...members, EVERYONE];
  }
  return orderRoster(agents.filter((agent) => agent.kind === "agent" && agent.id !== chat.id)).map(row);
}

/**
 * The @ being typed at the caret: where it starts and what follows it so far.
 * Null when the caret is not in one, or the @ is inside a word, as in an
 * address. A space ends it unless what is typed is still the start of a
 * name in `rows`, so "@Silence of" keeps the list open on Silence of Rhodes.
 */
export function mentionAt(text: string, caret: number, rows: readonly Mentionable[] = []): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf("@");
  if (start < 0) return null;
  const query = before.slice(start + 1);
  if (/\s/.test(query)) {
    const typed = query.toLowerCase();
    if (/\n/.test(query) || !rows.some((row) => row.everyone !== true && row.name.toLowerCase().startsWith(typed))) return null;
  }
  const previous = before[start - 1];
  if (previous != null && WORD.test(previous)) return null;
  return { start, query };
}

/** The ways a name can be written after @, as the host matches them: whole, without spaces, and its first word. */
function handlesOf(row: Mentionable): string[] {
  if (row.everyone === true) return ["everyone", "all"];
  const lower = row.name.trim().toLowerCase();
  if (lower.length === 0) return [];
  return [...new Set([lower, lower.replace(/\s+/g, ""), lower.split(/\s+/)[0] ?? lower])];
}

/** The rows for what was typed after @: names that start with it, then names with a word that does, then names that contain it. */
export function matchMentionables(rows: readonly Mentionable[], query: string, limit = 8): Mentionable[] {
  const needle = query.toLowerCase();
  const rank = (row: Mentionable): number => {
    const names = row.everyone === true ? ["everyone", "all"] : [row.name.toLowerCase()];
    if (names.some((name) => name.startsWith(needle) || name.replace(/\s+/g, "").startsWith(needle))) return 0;
    if (names.some((name) => name.split(/\s+/).some((word) => word.startsWith(needle)))) return 1;
    return names.some((name) => name.includes(needle)) ? 2 : -1;
  };
  return rows
    .map((row, index) => ({ row, index, rank: rank(row) }))
    .filter((scored) => scored.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map((scored) => scored.row);
}

/**
 * The text with the @ being typed, and the rest of that word, replaced by the
 * picked handle and a space; the caret lands after the space. `edit` is that
 * replacement, for moving the blocks already in the field, and `chip` is the
 * block the handle now is.
 */
export function insertMention(text: string, start: number, caret: number, row: Mentionable): { text: string; caret: number; edit: Edit; chip: Chip } {
  const end = caret + (/^[^\s@]*/.exec(text.slice(caret))?.[0].length ?? 0);
  const handle = `@${row.name}`;
  const rest = text.slice(end);
  const inserted = `${handle}${rest.startsWith(" ") ? "" : " "}`;
  return { text: `${text.slice(0, start)}${inserted}${rest}`, caret: start + handle.length + 1, edit: { at: start, removed: end - start, inserted: inserted.length }, chip: { start, end: start + handle.length, row } };
}

// --- mentions as blocks in the composer ---------------------------------------
//
// A mention picked from the list is one block in the field, as the
// prototype's editor makes it a node of its own: it is drawn as a pill, the
// caret steps over it rather than into it, and a deletion that reaches into
// it takes all of it, so a two-word name goes with one Backspace. The field
// stays a plain textarea (paste, spelling, a phone's keyboard all work as
// they do anywhere); the blocks are ranges over its text, carried through
// every edit here.

/** A picked mention's place in the text: `text.slice(start, end)` is its `@Name`. */
export interface Chip {
  readonly start: number;
  readonly end: number;
  /** Who was picked, so the pill can show their face. */
  readonly row?: Mentionable;
}

/** One change to the text: `removed` characters at `at` gave way to `inserted` new ones. */
export interface Edit {
  readonly at: number;
  readonly removed: number;
  readonly inserted: number;
}

/**
 * The edit that turned `before` into `after`. Within a run of the same
 * letter the place is ambiguous ("ann" to "an" loses one n or the other), so
 * the caret settles it: after typing or deleting it sits at the end of what
 * was inserted.
 */
export function editBetween(before: string, after: string, caret: number): Edit {
  const shortest = Math.min(before.length, after.length);
  let prefix = 0;
  while (prefix < shortest && before[prefix] === after[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < shortest - prefix && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]) suffix += 1;
  const removed = before.length - prefix - suffix;
  const inserted = after.length - prefix - suffix;
  const at = caret - inserted;
  const fits = at >= 0 && at <= prefix && before.slice(0, at) === after.slice(0, at) && before.slice(at + removed) === after.slice(at + inserted);
  return { at: fits ? at : prefix, removed, inserted };
}

/**
 * A deletion that reaches into a block, widened to take the whole of every
 * block it touches; any other edit as it was. Typing into a block is not
 * widened: the block simply stops being one (moveChips).
 */
export function widenDeletion(chips: readonly Chip[], edit: Edit): Edit {
  if (edit.inserted > 0 || edit.removed === 0) return edit;
  let from = edit.at;
  let to = edit.at + edit.removed;
  for (const chip of chips) {
    if (chip.start < to && chip.end > from) {
      from = Math.min(from, chip.start);
      to = Math.max(to, chip.end);
    }
  }
  return from === edit.at && to === edit.at + edit.removed ? edit : { at: from, removed: to - from, inserted: 0 };
}

/** The blocks after an edit: those before it stay, those after it move with the text, and one the edit cut into is plain text now. */
export function moveChips(chips: readonly Chip[], edit: Edit): Chip[] {
  const end = edit.at + edit.removed;
  const shift = edit.inserted - edit.removed;
  return chips.flatMap((chip) => {
    if (chip.end <= edit.at) return [chip];
    if (chip.start >= end) return [{ ...chip, start: chip.start + shift, end: chip.end + shift }];
    return [];
  });
}

/**
 * Where a caret that landed inside a block goes instead: a step from one
 * edge crosses to the other, so the arrows move over the block in one
 * press; anything else, like a tap, goes to the nearer edge.
 */
export function snapOutOfChips(chips: readonly Chip[], caret: number, previous: number): number {
  const inside = chips.find((chip) => caret > chip.start && caret < chip.end);
  if (inside == null) return caret;
  if (Math.abs(caret - previous) === 1) return caret > previous ? inside.end : inside.start;
  return caret - inside.start < inside.end - caret ? inside.start : inside.end;
}

/** The text cut at the blocks, for drawing their pills under the field. */
export function splitAtChips(text: string, chips: readonly Chip[]): Segment[] {
  const segments: Segment[] = [];
  let from = 0;
  for (const chip of [...chips].sort((a, b) => a.start - b.start)) {
    if (chip.start < from || chip.end > text.length) continue;
    if (chip.start > from) segments.push({ text: text.slice(from, chip.start), mention: false });
    segments.push({ text: text.slice(chip.start, chip.end), mention: true, ...(chip.row == null ? {} : { row: chip.row }) });
    from = chip.end;
  }
  if (from < text.length) segments.push({ text: text.slice(from), mention: false });
  return segments;
}

/** A stretch of a message: plain text, or an @-mention of someone in the list. */
export interface Segment {
  readonly text: string;
  readonly mention: boolean;
  /** Who the mention names: for its face, and in a sent message to open their chat. */
  readonly row?: Mentionable;
}

/**
 * A message cut into plain stretches and the mentions the host will read in
 * it, so the bubble can show which ones took. Each handle belongs to the
 * first row that has it: a full name to its own agent, a first word shared
 * by two names to the one listed first.
 */
export function splitMentions(text: string, rows: readonly Mentionable[]): Segment[] {
  const owners = new Map<string, Mentionable>();
  for (const row of rows) for (const handle of handlesOf(row)) if (!owners.has(handle)) owners.set(handle, row);
  const handles = [...owners.keys()].sort((a, b) => b.length - a.length);
  const lower = text.toLowerCase();
  const segments: Segment[] = [];
  let from = 0;
  for (let index = lower.indexOf("@"); index >= 0 && handles.length > 0; index = lower.indexOf("@", index + 1)) {
    const previous = lower[index - 1];
    if (previous != null && WORD.test(previous)) continue;
    const handle = handles.find((candidate) => lower.startsWith(candidate, index + 1) && !WORD.test(lower[index + 1 + candidate.length] ?? ""));
    if (handle == null) continue;
    if (index > from) segments.push({ text: text.slice(from, index), mention: false });
    const end = index + 1 + handle.length;
    segments.push({ text: text.slice(index, end), mention: true, row: owners.get(handle)! });
    from = end;
    index = end - 1;
  }
  if (from < text.length) segments.push({ text: text.slice(from), mention: false });
  return segments;
}
